import { assign, createMachine } from 'xstate';
import { OmitStrict } from '@tink/core/src/types';
import { ApplicationError } from '../../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { IncomeCheckResponse } from '../../models/incomeCheck';
import {
  IncomeCheckService,
  incomeCheckService as incomeCheckServiceImport,
} from '../../services/income-check';

export type IncomeCheckMachineViewProps = {
  engineVersion?: string;
  externalReference?: string;
  incomeCheckService?: IncomeCheckService;
  onCancel: () => void;
  isMultibanking?: boolean;
};

export type IncomeCheckMachineArgs = OmitStrict<IncomeCheckMachineViewProps, 'onCancel'>;

type Context = {
  error: ApplicationError | undefined;
  incomeCheckReport: IncomeCheckResponse | undefined;
};

type Services = {
  generateIncomeCheckReport: { data: IncomeCheckResponse };
};

export const getMachine = ({
  engineVersion,
  externalReference,
  incomeCheckService = incomeCheckServiceImport,
  isMultibanking,
}: IncomeCheckMachineArgs) =>
  createMachine(
    {
      predictableActionArguments: true,
      schema: {
        context: {} as Context,
        services: {} as Services,
      },
      context: {
        error: undefined,
        incomeCheckReport: undefined,
      },
      tsTypes: {} as import('./incomeCheckMachine.typegen').Typegen0,
      initial: 'auth',
      states: {
        auth: {
          on: {
            CREDENTIALS: [
              {
                cond: 'isMultibanking',
                target: 'addMultibankingProvider',
              },
              {
                target: 'createReport',
              },
            ],
          },
        },
        addMultibankingProvider: {
          on: {
            DONE_ADDING_BANKS: {
              target: 'createReport',
            },
          },
        },
        createReport: {
          invoke: {
            src: 'generateIncomeCheckReport',
            onDone: {
              target: 'success',
              actions: 'saveIncomeCheckReport',
            },
            onError: {
              target: 'error',
              actions: 'saveError',
            },
          },
        },
        success: {
          type: 'final',
        },
        error: { type: 'final' },
      },
    },
    {
      guards: {
        isMultibanking: () => Boolean(isMultibanking),
      },
      services: {
        generateIncomeCheckReport: () => {
          return incomeCheckService.createReport({
            externalReference,
            engineVersion,
          });
        },
      },
      actions: {
        saveIncomeCheckReport: assign({
          incomeCheckReport: (_, event) => event.data,
        }),
        saveError: assign({
          error: (_, event) => event.data as ApplicationError,
        }),
      },
    }
  );
